import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router';
import {
  createEvent,
  getApiErrorMessage,
  getEvent,
  importEventFromJson,
  importEventIntoExistingFromJson,
  listAllEventMedia,
  listCounties,
  listCountries,
  listEventTypes,
  listRegions,
  listSettlements,
  listTags,
  patchEvent,
  publishEvent,
  setEventCover,
  type EventStatus,
  uploadEventMedia,
} from '../lib/api';
import { CONTENT_BLOCK_MEDIA_TITLE_PREFIX, isContentBlockMediaTitle, stringifyContentBlocks } from '../lib/contentBlocks';
import { createRichContentEditor } from '../lib/richContentEditor';
import { CREATE_EVENT_TEMPLATE } from '../templates/createEventTemplate';

type PendingMedia = { file: File; url: string };

type Region = { id: number; country: number; name: string };
type County = { id: number; region: number; name: string };
type Settlement = { id: number; county: number; name: string };

const RU_TO_LAT: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

const transliterateRu = (value: string): string =>
  Array.from(value)
    .map((char) => RU_TO_LAT[char] ?? char)
    .join('');

const slugify = (value: string): string =>
  transliterateRu(value.toLowerCase())
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const toIsoFromLocal = (value: string): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const toLocalFromIso = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

const normalizeCoordinate = (
  raw: string,
  min: number,
  max: number
): { value: string; numberValue: number } | null => {
  const parsed = Number(raw.trim().replace(',', '.'));
  if (Number.isNaN(parsed) || parsed < min || parsed > max) return null;
  const fixed = parsed.toFixed(6);
  return { value: fixed, numberValue: Number(fixed) };
};

export default function CreateEventPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => CREATE_EVENT_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const form = root.querySelector<HTMLFormElement>('#create-event-form');
    const alert = root.querySelector<HTMLElement>('#create-event-alert');
    const alertTitle = root.querySelector<HTMLElement>('#create-event-alert-title');
    const alertText = root.querySelector<HTMLElement>('#create-event-alert-text');
    const titleInput = root.querySelector<HTMLInputElement>('#event-title');
    const slugInput = root.querySelector<HTMLInputElement>('#event-slug');
    const typeSelect = root.querySelector<HTMLSelectElement>('#event-type');
    const descriptionInput = root.querySelector<HTMLTextAreaElement>('#event-description');
    const contentBlocksInput = root.querySelector<HTMLTextAreaElement>('#event-content-blocks');
    const contentBlocksEditorRoot = root.querySelector<HTMLElement>('#event-content-blocks-editor');
    const startsAtInput = root.querySelector<HTMLInputElement>('#event-starts-at');
    const endsAtInput = root.querySelector<HTMLInputElement>('#event-ends-at');
    const timezoneSelect = root.querySelector<HTMLSelectElement>('#event-timezone');
    const locationNameInput = root.querySelector<HTMLInputElement>('#event-location-name');
    const countrySelect = root.querySelector<HTMLSelectElement>('#event-country');
    const regionSelect = root.querySelector<HTMLSelectElement>('#event-region');
    const countySelect = root.querySelector<HTMLSelectElement>('#event-county');
    const settlementSelect = root.querySelector<HTMLSelectElement>('#event-settlement');
    const latitudeInput = root.querySelector<HTMLInputElement>('#event-latitude');
    const longitudeInput = root.querySelector<HTMLInputElement>('#event-longitude');
    const capacityInput = root.querySelector<HTMLInputElement>('#event-capacity');
    const priceInput = root.querySelector<HTMLInputElement>('#event-price-from');
    const isFreeInput = root.querySelector<HTMLInputElement>('#event-is-free');
    const isFeaturedInput = root.querySelector<HTMLInputElement>('#event-is-featured');
    const coverInput = root.querySelector<HTMLInputElement>('#event-cover-file');
    const coverFileName = root.querySelector<HTMLElement>('#event-cover-file-name');
    const coverPreview = root.querySelector<HTMLElement>('#event-cover-preview');
    const galleryInput = root.querySelector<HTMLInputElement>('#event-gallery-files');
    const galleryFileName = root.querySelector<HTMLElement>('#event-gallery-file-name');
    const galleryPreview = root.querySelector<HTMLElement>('#event-gallery-preview');
    const mapContainer = root.querySelector<HTMLElement>('#event-map');
    const mapPlaceholder = root.querySelector<HTMLElement>('#event-map-placeholder');
    const actions = root.querySelector<HTMLElement>('#event-actions');
    const cancelBtn = root.querySelector<HTMLButtonElement>('#event-cancel-btn');
    const importBtn = root.querySelector<HTMLButtonElement>('#event-import-json-btn');
    const saveDraftBtn = root.querySelector<HTMLButtonElement>('#event-save-draft-btn');
    const publishBtn = root.querySelector<HTMLButtonElement>('#event-publish-btn');

    if (
      !form ||
      !alert ||
      !alertTitle ||
      !alertText ||
      !titleInput ||
      !slugInput ||
      !typeSelect ||
      !descriptionInput ||
      !contentBlocksInput ||
      !contentBlocksEditorRoot ||
      !startsAtInput ||
      !endsAtInput ||
      !timezoneSelect ||
      !locationNameInput ||
      !countrySelect ||
      !regionSelect ||
      !countySelect ||
      !settlementSelect ||
      !latitudeInput ||
      !longitudeInput ||
      !capacityInput ||
      !priceInput ||
      !isFreeInput ||
      !isFeaturedInput ||
      !coverInput ||
      !coverFileName ||
      !coverPreview ||
      !galleryInput ||
      !galleryFileName ||
      !galleryPreview ||
      !mapContainer ||
      !mapPlaceholder ||
      !actions ||
      !cancelBtn ||
      !importBtn ||
      !saveDraftBtn ||
      !publishBtn
    ) {
      return;
    }

    const controls = root.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      '#create-event-form input, #create-event-form select, #create-event-form textarea'
    );
    for (const control of controls) {
      if (control instanceof HTMLInputElement) {
        const type = control.type.toLowerCase();
        if (
          type === 'checkbox' ||
          type === 'radio' ||
          type === 'file' ||
          type === 'hidden' ||
          control.classList.contains('sr-only')
        ) {
          continue;
        }
      }
      control.classList.add('px-3');
      if (control instanceof HTMLTextAreaElement) {
        control.classList.add('py-2');
      }
    }

    timezoneSelect.innerHTML = `
      <option value="Europe/Minsk">(GMT+03:00) Europe/Minsk</option>
      <option value="UTC">(GMT+00:00) UTC</option>
      <option value="Europe/Berlin">(GMT+01:00) Central European</option>
    `;

    const editIdRaw = searchParams.get('id');
    const editId = editIdRaw ? Number(editIdRaw) : NaN;
    const isEdit = Number.isFinite(editId) && editId > 0;

    let disposed = false;
    let slugTouched = false;
    let currentEventId: number | null = isEdit ? editId : null;
    let selectedCover: File | null = null;
    let selectedGallery: PendingMedia[] = [];
    let existingGallery: Array<{ id: number; url: string }> = [];
    let currentCoverObjectUrl: string | null = null;
    let mapInstance: {
      remove: () => void;
      panTo: (coords: [number, number]) => void;
      getZoom: () => number;
      setView: (coords: [number, number], zoom?: number, options?: { animate?: boolean }) => void;
      invalidateSize: () => void;
      on: (event: string, callback: (payload: any) => void) => void;
    } | null = null;
    let markerInstance: {
      setLatLng: (coords: [number, number]) => void;
      on: (event: string, callback: (payload: any) => void) => void;
    } | null = null;
    let mode: 'draft' | 'publish' = 'draft';
    let currentStatus: EventStatus | 'new' = 'new';
    let availableContentTags: Array<{ id: number; name: string; slug: string }> = [];
    const contentBlocksEditor = createRichContentEditor({
      root: contentBlocksEditorRoot,
      onChange: (blocks) => {
        contentBlocksInput.value = stringifyContentBlocks(blocks);
      },
      getTagOptions: () => availableContentTags,
      uploadImage: async (file) => {
        if (!currentEventId) {
          throw new Error('Сначала сохраните событие как черновик, затем загружайте изображения в блоки.');
        }
        const media = await uploadEventMedia(currentEventId, { file, title: `${CONTENT_BLOCK_MEDIA_TITLE_PREFIX}${file.name}` });
        return media.file_url;
      },
    });

    let regions: Region[] = [];
    let counties: County[] = [];
    let settlements: Settlement[] = [];

    const setButtonsLoading = (loading: boolean) => {
      saveDraftBtn.disabled = loading;
      publishBtn.disabled = loading;
      importBtn.disabled = loading;
      saveDraftBtn.classList.toggle('opacity-60', loading);
      publishBtn.classList.toggle('opacity-60', loading);
      importBtn.classList.toggle('opacity-60', loading);
    };

    const setImportLoading = (loading: boolean) => {
      importBtn.disabled = loading;
      saveDraftBtn.disabled = loading;
      publishBtn.disabled = loading;
      importBtn.innerHTML = loading
        ? '<span class="material-symbols-outlined text-sm animate-spin">progress_activity</span> Importing...'
        : '<span class="material-symbols-outlined text-sm">upload_file</span> Import JSON';
    };

    const openJsonImportDialog = (): Promise<string | null> =>
      new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'fixed inset-0 z-[1500] flex items-center justify-center bg-slate-950/70 p-4';
        overlay.innerHTML = `
          <div class="w-full max-w-3xl rounded-xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div class="mb-3 flex items-center justify-between">
              <h3 class="text-base font-bold text-slate-900">Import Event JSON</h3>
              <button data-import-close="true" class="size-8 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100" type="button">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
            <p class="mb-3 text-xs text-slate-500">Paste full JSON payload for event import.</p>
            <textarea data-import-json="true" class="h-72 w-full rounded-lg border border-slate-300 bg-slate-50 p-3 font-mono text-xs focus:border-primary focus:ring-primary" placeholder='{"title":"...","starts_at":"..."}'></textarea>
            <div class="mt-4 flex justify-end gap-2">
              <button data-import-cancel="true" class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100" type="button">Cancel</button>
              <button data-import-confirm="true" class="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600" type="button">Import</button>
            </div>
          </div>
        `;
        document.body.append(overlay);
        document.body.classList.add('overflow-hidden');

        const textarea = overlay.querySelector<HTMLTextAreaElement>('textarea[data-import-json="true"]');
        const close = () => {
          overlay.remove();
          document.body.classList.remove('overflow-hidden');
        };

        const onClick = (event: Event) => {
          const target = event.target as HTMLElement;
          if (target === overlay || target.closest('[data-import-close="true"]') || target.closest('[data-import-cancel="true"]')) {
            close();
            resolve(null);
            return;
          }
          if (target.closest('[data-import-confirm="true"]')) {
            const value = textarea?.value ?? '';
            close();
            resolve(value);
          }
        };

        const onKeydown = (event: KeyboardEvent) => {
          if (event.key !== 'Escape') return;
          document.removeEventListener('keydown', onKeydown);
          close();
          resolve(null);
        };

        overlay.addEventListener('click', onClick, { once: false });
        document.addEventListener('keydown', onKeydown, { once: true });
        window.setTimeout(() => textarea?.focus(), 0);
      });

    const showAlert = (kind: 'error' | 'success', title: string, text: string) => {
      alert.classList.remove('hidden', 'bg-rose-50', 'border-rose-200', 'bg-emerald-50', 'border-emerald-200');
      if (kind === 'success') {
        alert.classList.add('bg-emerald-50', 'border-emerald-200');
        alertTitle.className = 'text-sm font-semibold text-emerald-800';
        alertText.className = 'text-sm text-emerald-700 opacity-80';
      } else {
        alert.classList.add('bg-rose-50', 'border-rose-200');
        alertTitle.className = 'text-sm font-semibold text-rose-800';
        alertText.className = 'text-sm text-rose-700 opacity-80';
      }
      alertTitle.textContent = title;
      alertText.textContent = text;
    };

    const toUiErrorMessage = (error: unknown, fallback: string): string => {
      const details = (error as { response?: { data?: { error?: { details?: Record<string, unknown> } } } })?.response?.data?.error
        ?.details;
      if (details && typeof details === 'object' && !Array.isArray(details)) {
        const firstKey = Object.keys(details)[0];
        if (firstKey) {
          const firstValue = (details as Record<string, unknown>)[firstKey];
          if (Array.isArray(firstValue) && firstValue[0]) {
            const message = String(firstValue[0]);
            if (firstKey === 'status' && message.includes('Only draft or pending events can be published')) {
              return 'Событие уже опубликовано. Используйте Save Draft/Save Changes для обновления без смены статуса.';
            }
            return `${firstKey}: ${message}`;
          }
          if (typeof firstValue === 'string') {
            return `${firstKey}: ${firstValue}`;
          }
        }
      }
      return getApiErrorMessage(error, fallback);
    };

    const toOptionalNumber = (value: string): number | undefined => {
      const trimmed = value.trim();
      if (!trimmed) return undefined;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const fillSelect = (
      select: HTMLSelectElement,
      items: Array<{ id: number; name: string }>,
      placeholder: string
    ) => {
      select.innerHTML = `<option value="">${placeholder}</option>`;
      for (const item of items) {
        select.insertAdjacentHTML('beforeend', `<option value="${item.id}">${item.name}</option>`);
      }
    };

    const renderGalleryPreview = () => {
      const existingHtml = existingGallery
        .slice(0, 10)
        .map(
          (item) => `
          <div class="aspect-square rounded-xl bg-slate-100 border border-slate-200 overflow-hidden">
            <img class="w-full h-full object-cover" src="${item.url}" alt="Event media ${item.id}"/>
          </div>
        `
        )
        .join('');

      const pendingHtml = selectedGallery
        .slice(0, 10)
        .map(
          (item) => `
          <div class="aspect-square rounded-xl bg-slate-100 border border-slate-200 overflow-hidden">
            <img class="w-full h-full object-cover" src="${item.url}" alt="${item.file.name}"/>
          </div>
        `
        )
        .join('');

      galleryFileName.textContent = selectedGallery.length ? `${selectedGallery.length} new file(s) selected` : '';
      galleryPreview.innerHTML =
        existingHtml + pendingHtml || '<div class="text-xs text-slate-500">No gallery images selected.</div>';
    };

    const rerenderRegions = () => {
      const countryId = toOptionalNumber(countrySelect.value);
      const filtered = countryId ? regions.filter((item) => item.country === countryId) : regions;
      fillSelect(regionSelect, filtered, 'Select region');
      fillSelect(countySelect, [], 'Select county');
      fillSelect(settlementSelect, [], 'Select settlement');
    };

    const rerenderCounties = () => {
      const regionId = toOptionalNumber(regionSelect.value);
      const filtered = regionId ? counties.filter((item) => item.region === regionId) : counties;
      fillSelect(countySelect, filtered, 'Select county');
      fillSelect(settlementSelect, [], 'Select settlement');
    };

    const rerenderSettlements = () => {
      const countyId = toOptionalNumber(countySelect.value);
      const filtered = countyId ? settlements.filter((item) => item.county === countyId) : settlements;
      fillSelect(settlementSelect, filtered, 'Select settlement');
    };

    const loadDictionaries = async () => {
      const [types, countriesData, regionsData, countiesData, settlementsData, tagsData] = await Promise.all([
        listEventTypes({ ordering: 'sort_order,name', is_active: true }),
        listCountries(),
        listRegions(),
        listCounties(),
        listSettlements(),
        listTags(),
      ]);
      if (disposed) return;

      typeSelect.innerHTML = '<option value="">Select event type</option>';
      for (const item of types.results) {
        typeSelect.insertAdjacentHTML('beforeend', `<option value="${item.id}">${item.name}</option>`);
      }

      fillSelect(countrySelect, countriesData.map((item) => ({ id: item.id, name: item.name })), 'Select country');
      regions = regionsData.map((item) => ({ id: item.id, name: item.name, country: (item as { country: number }).country }));
      counties = countiesData.map((item) => ({ id: item.id, name: item.name, region: (item as { region: number }).region }));
      settlements = settlementsData.map((item) => ({
        id: item.id,
        name: item.name,
        county: (item as { county: number }).county,
      }));
      availableContentTags = tagsData.map((item) => ({ id: item.id, name: item.name, slug: item.slug }));

      fillSelect(regionSelect, [], 'Select region');
      fillSelect(countySelect, [], 'Select county');
      fillSelect(settlementSelect, [], 'Select settlement');
    };

    const fillFormForEdit = async () => {
      if (!isEdit) return;
      const event = await getEvent(editId);
      currentEventId = event.id;
      if (disposed) return;
      currentStatus = event.status;
      titleInput.value = event.title || '';
      slugInput.value = event.slug || '';
      descriptionInput.value = event.description || '';
      contentBlocksEditor.setBlocks(event.content_blocks);
      startsAtInput.value = toLocalFromIso(event.starts_at);
      endsAtInput.value = toLocalFromIso(event.ends_at);
      timezoneSelect.value = event.timezone || 'UTC';
      locationNameInput.value = event.location_name || '';
      latitudeInput.value = event.latitude || '';
      longitudeInput.value = event.longitude || '';
      capacityInput.value = event.capacity ? String(event.capacity) : '';
      priceInput.value = event.price_from || '';
      isFreeInput.checked = event.is_free;
      isFeaturedInput.checked = event.is_featured;

      const selectedOrigin = form.querySelector<HTMLInputElement>(`input[name="event-origin"][value="${event.origin_type}"]`);
      if (selectedOrigin) selectedOrigin.checked = true;
      if (event.event_type) typeSelect.value = String(event.event_type);

      if (event.country) {
        countrySelect.value = String(event.country);
        rerenderRegions();
      }
      if (event.region) {
        regionSelect.value = String(event.region);
        rerenderCounties();
      }
      if (event.county) {
        countySelect.value = String(event.county);
        rerenderSettlements();
      }
      if (event.settlement) {
        settlementSelect.value = String(event.settlement);
      }

      if (event.cover_image) {
        coverPreview.innerHTML = `<img class="h-32 w-full max-w-xs rounded-lg object-cover border border-slate-200" src="${event.cover_image}" alt="Cover preview"/>`;
      }
      if (event.status !== 'draft' && event.status !== 'pending_review') {
        saveDraftBtn.textContent = 'Save Changes';
        publishBtn.classList.add('hidden');
      }

      const mediaPayload = await listAllEventMedia(editId);
      if (disposed) return;
      existingGallery = mediaPayload.results
        .filter((item) => !isContentBlockMediaTitle(item.title))
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((item) => ({ id: item.id, url: item.file_url }));
      renderGalleryPreview();
    };

    const onTitleInput = () => {
      if (slugTouched) return;
      slugInput.value = slugify(titleInput.value);
    };

    const onSlugInput = () => {
      slugTouched = slugInput.value.trim().length > 0;
    };

    const onCountryChange = () => rerenderRegions();
    const onRegionChange = () => rerenderCounties();
    const onCountyChange = () => rerenderSettlements();

    const onCoverChange = () => {
      selectedCover = coverInput.files?.[0] ?? null;
      coverFileName.textContent = selectedCover ? selectedCover.name : '';
      if (currentCoverObjectUrl) {
        URL.revokeObjectURL(currentCoverObjectUrl);
        currentCoverObjectUrl = null;
      }
      if (selectedCover) {
        currentCoverObjectUrl = URL.createObjectURL(selectedCover);
        coverPreview.innerHTML = `<img class="h-32 w-full max-w-xs rounded-lg object-cover border border-slate-200" src="${currentCoverObjectUrl}" alt="Cover preview"/>`;
      }
    };

    const onGalleryChange = () => {
      const files = Array.from(galleryInput.files ?? []);
      for (const file of files) {
        selectedGallery.push({ file, url: URL.createObjectURL(file) });
      }
      galleryInput.value = '';
      renderGalleryPreview();
    };

    const onCoordinatesInput = () => {
      const lat = normalizeCoordinate(latitudeInput.value, -90, 90);
      const lon = normalizeCoordinate(longitudeInput.value, -180, 180);
      if (!lat || !lon || !markerInstance || !mapInstance) return;
      markerInstance.setLatLng([lat.numberValue, lon.numberValue]);
      mapInstance.setView([lat.numberValue, lon.numberValue], mapInstance.getZoom(), { animate: false });
    };

    const ensureLeaflet = async (): Promise<void> => {
      if ((window as unknown as { L?: unknown }).L) return;
      const cssHref = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      const scriptSrc = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

      if (!document.querySelector(`link[href="${cssHref}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssHref;
        document.head.append(link);
      }

      if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = scriptSrc;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Leaflet load failed'));
          document.body.append(script);
        });
      } else if (!(window as unknown as { L?: unknown }).L) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 150));
      }
    };

    const initMap = async () => {
      try {
        await ensureLeaflet();
      } catch {
        return;
      }
      if (disposed) return;
      const L = (window as unknown as { L?: any }).L;
      if (!L) return;

      mapPlaceholder.classList.add('hidden');

      const initialLat = normalizeCoordinate(latitudeInput.value || '53.900000', -90, 90);
      const initialLon = normalizeCoordinate(longitudeInput.value || '27.566700', -180, 180);
      const lat = initialLat?.numberValue ?? 53.9;
      const lon = initialLon?.numberValue ?? 27.5667;

      const map = L.map(mapContainer, { zoomControl: true }).setView([lat, lon], 10);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);
      const marker = L.marker([lat, lon], { draggable: true }).addTo(map);

      mapContainer.style.zIndex = '0';
      mapContainer.style.isolation = 'isolate';
      mapContainer.querySelectorAll<HTMLElement>('.leaflet-pane, .leaflet-top, .leaflet-bottom').forEach((el) => {
        el.style.zIndex = '1';
      });

      map.on('click', (e: { latlng: { lat: number; lng: number } }) => {
        const nextLat = e.latlng.lat.toFixed(6);
        const nextLon = e.latlng.lng.toFixed(6);
        latitudeInput.value = nextLat;
        longitudeInput.value = nextLon;
        marker.setLatLng([Number(nextLat), Number(nextLon)]);
      });

      marker.on('dragend', (e: { target: { getLatLng: () => { lat: number; lng: number } } }) => {
        const point = e.target.getLatLng();
        latitudeInput.value = point.lat.toFixed(6);
        longitudeInput.value = point.lng.toFixed(6);
      });

      mapInstance = map;
      markerInstance = marker;
      window.setTimeout(() => map.invalidateSize(), 120);
    };

    const buildPayload = (): Record<string, unknown> => {
      const startsAt = toIsoFromLocal(startsAtInput.value);
      if (!startsAt) throw new Error('Start date is required.');

      const lat = latitudeInput.value.trim();
      const lon = longitudeInput.value.trim();
      const latNormalized = lat ? normalizeCoordinate(lat, -90, 90) : null;
      const lonNormalized = lon ? normalizeCoordinate(lon, -180, 180) : null;
      if ((lat && !latNormalized) || (lon && !lonNormalized)) {
        throw new Error('Invalid coordinates. Latitude: [-90..90], Longitude: [-180..180].');
      }

      const endsAt = toIsoFromLocal(endsAtInput.value);
      const parsedBlocks = contentBlocksEditor.getBlocks();
      return {
        title: titleInput.value.trim(),
        slug: slugInput.value.trim() || undefined,
        description: descriptionInput.value.trim(),
        content_format: parsedBlocks?.length ? 'blocks_v1' : undefined,
        content_blocks: parsedBlocks || undefined,
        origin_type: form.querySelector<HTMLInputElement>('input[name="event-origin"]:checked')?.value || 'official',
        event_type: toOptionalNumber(typeSelect.value),
        starts_at: startsAt,
        ends_at: endsAt || null,
        timezone: timezoneSelect.value || 'UTC',
        location_name: locationNameInput.value.trim(),
        country: toOptionalNumber(countrySelect.value),
        region: toOptionalNumber(regionSelect.value),
        county: toOptionalNumber(countySelect.value),
        settlement: toOptionalNumber(settlementSelect.value),
        latitude: latNormalized?.value || null,
        longitude: lonNormalized?.value || null,
        capacity: toOptionalNumber(capacityInput.value),
        is_free: isFreeInput.checked,
        is_featured: isFeaturedInput.checked,
        price_from: isFreeInput.checked ? null : priceInput.value.trim() || null,
      };
    };

    const submit = async () => {
      setButtonsLoading(true);
      try {
        const payload = buildPayload();
        const event = isEdit ? await patchEvent(editId, payload) : await createEvent(payload as any);
        currentEventId = event.id;

        if (selectedCover) {
          await setEventCover(event.id, selectedCover);
        }

        if (selectedGallery.length) {
          for (let i = 0; i < selectedGallery.length; i += 1) {
            await uploadEventMedia(event.id, { file: selectedGallery[i].file, sort_order: i });
          }
        }

        let publishedNow = false;
        if (mode === 'publish') {
          if (!(isEdit && currentStatus === 'published')) {
            await publishEvent(event.id);
            publishedNow = true;
          }
          currentStatus = 'published';
        }

        showAlert(
          'success',
          mode === 'publish' ? 'Saved' : 'Saved',
          mode === 'publish'
            ? publishedNow
              ? 'Событие опубликовано.'
              : 'Событие обновлено и осталось в статусе Published.'
            : currentStatus === 'published'
              ? 'Изменения сохранены. Событие осталось Published.'
              : 'Черновик сохранен.'
        );

        setTimeout(() => {
          if (!disposed) navigate('/events');
        }, 450);
      } catch (error) {
        const fallback = error instanceof Error ? error.message : 'Failed to save event.';
        showAlert('error', 'Request Failed', toUiErrorMessage(error, fallback));
      } finally {
        if (!disposed) setButtonsLoading(false);
      }
    };

    const onImportJson = async () => {
      const raw = await openJsonImportDialog();
      if (raw === null) return;
      if (!raw.trim()) {
        showAlert('error', 'Import failed', 'JSON payload is empty.');
        return;
      }

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        showAlert('error', 'Import failed', 'Invalid JSON format.');
        return;
      }

      setImportLoading(true);
      try {
        if (currentEventId) {
          const updated = await importEventIntoExistingFromJson(currentEventId, parsed);
          showAlert('success', 'Event updated', `Event updated from JSON: #${updated.id}.`);
          await fillFormForEdit();
        } else {
          const created = await importEventFromJson(parsed);
          showAlert('success', 'Event imported', `Event imported: #${created.id}. Opening editor...`);
          window.setTimeout(() => navigate(`/events/create?id=${created.id}`), 250);
        }
      } catch (error) {
        showAlert('error', 'Import failed', toUiErrorMessage(error, 'Failed to import JSON event.'));
      } finally {
        setImportLoading(false);
      }
    };

    const onImportJsonClick = () => {
      void onImportJson();
    };

    const onDraftClick = () => {
      mode = 'draft';
      void submit();
    };

    const onPublishClick = () => {
      mode = 'publish';
      void submit();
    };

    const onCancelClick = () => {
      navigate('/events');
    };

    const onFormSubmit = (event: SubmitEvent) => {
      event.preventDefault();
      onDraftClick();
    };

    titleInput.addEventListener('input', onTitleInput);
    slugInput.addEventListener('input', onSlugInput);
    countrySelect.addEventListener('change', onCountryChange);
    regionSelect.addEventListener('change', onRegionChange);
    countySelect.addEventListener('change', onCountyChange);
    latitudeInput.addEventListener('input', onCoordinatesInput);
    longitudeInput.addEventListener('input', onCoordinatesInput);
    coverInput.addEventListener('change', onCoverChange);
    galleryInput.addEventListener('change', onGalleryChange);
    cancelBtn.addEventListener('click', onCancelClick);
    importBtn.addEventListener('click', onImportJsonClick);
    saveDraftBtn.addEventListener('click', onDraftClick);
    publishBtn.addEventListener('click', onPublishClick);
    form.addEventListener('submit', onFormSubmit);

    renderGalleryPreview();

    void (async () => {
      try {
        await loadDictionaries();
        await fillFormForEdit();
        await initMap();
      } catch (error) {
        showAlert('error', 'Load Failed', getApiErrorMessage(error, 'Failed to load dictionaries.'));
      } finally {
        actions.classList.remove('invisible');
      }
    })();

    return () => {
      disposed = true;
      titleInput.removeEventListener('input', onTitleInput);
      slugInput.removeEventListener('input', onSlugInput);
      countrySelect.removeEventListener('change', onCountryChange);
      regionSelect.removeEventListener('change', onRegionChange);
      countySelect.removeEventListener('change', onCountyChange);
      latitudeInput.removeEventListener('input', onCoordinatesInput);
      longitudeInput.removeEventListener('input', onCoordinatesInput);
      coverInput.removeEventListener('change', onCoverChange);
      galleryInput.removeEventListener('change', onGalleryChange);
      cancelBtn.removeEventListener('click', onCancelClick);
      importBtn.removeEventListener('click', onImportJsonClick);
      saveDraftBtn.removeEventListener('click', onDraftClick);
      publishBtn.removeEventListener('click', onPublishClick);
      form.removeEventListener('submit', onFormSubmit);
      contentBlocksEditor.destroy();
      if (mapInstance) mapInstance.remove();
      if (currentCoverObjectUrl) URL.revokeObjectURL(currentCoverObjectUrl);
      for (const media of selectedGallery) URL.revokeObjectURL(media.url);
    };
  }, [navigate, searchParams]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Create Event</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
